import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import {
  getFixedWhereClause,
  getTenantSlugFromURL,
} from '@modules/common/utils';
import {
  constructMarkdownFromFeedbackSummaryResponse,
  convertMarkdownToHTML,
  trackWisdomEvents,
} from '@modules/oracle/utils';
import {
  denormaliseQuery,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { captureException } from '@sentry/react';
import {
  AnalysisUseCase,
  FeedbackSummaryMessage,
  LabelType,
  OperationType,
  WebSocketMessage,
} from 'generated/graphql';
import pluralize from 'pluralize';
import { useCallback } from 'react';
import { v4 } from 'uuid';
import { getAnalysisLink } from 'v2/core/services/analysis/link';
import { QueryContainer } from 'v2/core/services/notebook/types';
import fromWhereClause from 'v2/core/services/queryBuilder/fromWhereClause';
import toWhereClause from 'v2/core/services/queryBuilder/toWhereClause';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import pathJoin from 'v2/core/utils/path';
import { ROOT_UUID } from 'v2/querybuilder/constants/query';
import normaliseQuery from 'v2/querybuilder/services/denormalise/normaliseQuery';
import {
  NormalisedFilter,
  NormalisedNode,
} from 'v2/querybuilder/types/querystore';
import { getUseInSearchLink } from '../../feed/utils/getUseInSearchLink';

interface CopySummaryAsFormattedTextProps {
  messages: WebSocketMessage[];
  queryContainer: QueryContainer;
  nodes?: Record<string, NormalisedNode>;
  rootID?: string;
  prompt?: string;
}

interface GetUpdatedWhereClauseProps {
  labelType: LabelType;
  labelID: string;
  rootID?: string;
  nodes?: Record<string, NormalisedNode>;
  queryContainer: QueryContainer;
}

const getSearchLink = (
  queryContainer: QueryContainer,
  whereClause: WhereClause,
  tenantSlug: string | undefined,
  isSearchToFeedEnabled: boolean,
  prompt?: string,
) => {
  const searchPageLinkParams = getUseInSearchLink({
    where: whereClause,
    duration: queryContainer.DateRange as DateTimeRange,
    isSearchToFeedEnabled,
    prompt,
  });

  const searchPageLinkString = pathJoin(
    window.location.origin,
    tenantSlug ?? '',
    searchPageLinkParams.pathname,
  );

  return {
    searchPageLinkParams,
    searchPageLinkString,
  };
};

export const getUpdatedWhereClause = ({
  labelType,
  labelID,
  rootID,
  nodes,
  queryContainer,
}: GetUpdatedWhereClauseProps) => {
  const node: NormalisedFilter = {
    field: labelType === LabelType.Reason ? 'reason' : 'entity',
    id: v4(),
    type: 'filter',
    operator: OperationType.And,
    value: {
      type: 'string[]',
      values: [{ type: 'string', value: labelID }],
    },
    parent: rootID ?? ROOT_UUID,
  };

  const updatedNodes = upsertQueryFilterNode({
    nodesByID:
      nodes ?? normaliseQuery(fromWhereClause(queryContainer.WhereClause)),
    parentID: rootID ?? ROOT_UUID,
    node,
  });

  const whereClause = getFixedWhereClause(
    toWhereClause(denormaliseQuery(updatedNodes, rootID ?? ROOT_UUID)),
  );

  return whereClause;
};

export const useCopySummaryAsFormattedText = ({
  messages,
  queryContainer,
  nodes,
  rootID,
  prompt,
}: CopySummaryAsFormattedTextProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  // const getLabelByResponseID = useCallback(() => {
  //   const labelByResponseID: Record<string, FeedbackSummaryLabel> = {};
  //   messages.forEach((message) => {
  //     const { feedbackSummary: { token } = {} } = message;
  //     if (token?.details?.label && message.feedbackSummary?.id) {
  //       labelByResponseID[message.feedbackSummary.id] = token.details.label;
  //     }
  //   });
  //   return labelByResponseID;
  // }, [messages]);

  const getMarkdownForFeedbackSummaryMessage = useCallback(
    (message: FeedbackSummaryMessage | undefined) => {
      const token = message?.token;
      const markdownContent = token?.content;

      // const labelByResponseID = getLabelByResponseID();

      if (token?.details) {
        const { examples, label } = token.details;

        if (label && label?.details.displayName) {
          return `**${label.details.displayName}**`;
        }

        if (token.details.artefact) {
          const artefact = token.details.artefact;
          const feedbackCount = artefact.feedbackCount ?? 0;
          const recordString = pluralize('Record', feedbackCount, true);

          let artefactLink = getAnalysisLink({
            name: artefact.analysis?.title ?? '',
            token: artefact.analysis?.viewToken ?? '',
            tenantSlug: getTenantSlugFromURL(),
            useCase: artefact.analysis?.useCase ?? AnalysisUseCase.Default,
          });

          return ` [(${recordString})](${artefactLink})`;
        }

        if (examples && examples?.labelExample?.recordIDs) {
          /** Commented the code for reference */

          // const label = examples.labelExample.forLabel;

          // const whereClause = getUpdatedWhereClause({
          //   labelType: label?.details.type,
          //   labelID: label?.details.id,
          //   rootID,
          //   nodes,
          //   queryContainer,
          // });

          // const { searchPageLinkString, searchPageLinkParams } = getSearchLink(
          //   queryContainer,
          //   whereClause,
          //   tenantSlug,
          // );

          const feedbackCount = examples.count;
          const recordString = feedbackCount > 1 ? 'Records' : 'Record';
          // return ` [(${feedbackCount} ${recordString})](${searchPageLinkString}?${searchPageLinkParams.search})`;

          return ` (${feedbackCount} ${recordString})`;
        }

        if (examples?.claimExample?.records) {
          return examples.claimExample.records
            .map(
              (claimExampleRecord, index) =>
                `[[${index + 1}]](${
                  window.location.origin
                }/${getTenantSlugFromURL()}/record/${
                  claimExampleRecord.recordID
                }?unfurl=false)`,
            )
            .join(', ')
            .trim();
        }
        return markdownContent ?? '';
      }
      return markdownContent ?? '';
    },
    [],
  );

  const getMarkdown = useCallback(() => {
    const constructedMarkdown = constructMarkdownFromFeedbackSummaryResponse(
      messages,
      getMarkdownForFeedbackSummaryMessage,
    );

    const { searchPageLinkString, searchPageLinkParams } = getSearchLink(
      queryContainer,
      queryContainer.WhereClause,
      tenantSlug,
      isSearchToFeedEnabled,
      prompt,
    );

    return `${constructedMarkdown} \n \n [See on Enterpret](${searchPageLinkString}?${searchPageLinkParams.search})`;
  }, [
    getMarkdownForFeedbackSummaryMessage,
    messages,
    queryContainer,
    tenantSlug,
    isSearchToFeedEnabled,
    prompt,
  ]);

  return useCallback(async () => {
    try {
      const markdown = getMarkdown();
      const html = convertMarkdownToHTML(markdown);
      const markdownWithoutLinks = markdown.replace(
        /\[.*?\]\(.*?\), |\[.*?\]\(.*?\)/g,
        '',
      ); // Remove links

      if ('ClipboardItem' in window) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([markdownWithoutLinks], {
            type: 'text/plain',
          }),
        });
        await navigator.clipboard.write([item]);
        showSuccessNotification({
          contentComponent: 'Summary copied to clipboard',
          props: {},
        });
      } else {
        await navigator.clipboard.writeText(markdownWithoutLinks);
        showSuccessNotification({
          contentComponent:
            'Unformatted summary copied to clipboard. Your browser does not support copying formatted text.',
          props: {},
        });
      }

      try {
        trackWisdomEvents({
          eventName: 'Wisdom - Copy Response Clicked',
          response: markdownWithoutLinks,
        });
      } catch {}

      return markdownWithoutLinks;
    } catch (e) {
      captureException(e);
      showErrorNotification({
        contentComponent: 'Failed to copy summary to clipboard',
        props: {},
      });
    }
  }, [getMarkdown, showErrorNotification, showSuccessNotification]);
};
